import React, { Component } from "react";
import { Row, Col, Modal } from "react-bootstrap";
import Select from "react-select";
import "./_singleRequest.scss";
import "./_studentPermission.scss";
import gql from "graphql-tag";
import moment from "moment";
import { graphql } from "react-apollo";
import * as Cookies from "es-cookie";
import { withApollo } from "react-apollo";
import * as compose from "lodash.flowright";
import { withRouter } from "react-router-dom";

const ADD_SINGLE_REQUEST_DETAILS = gql`
  mutation($params: AddPermissionDetails) {
    createIndividualPermissions(params: $params)
  }`;

// All students for the institute → dropdown of "student name - admission no",
// with student + parent details shown once one is picked.
const GET_STUDENTS = gql`
  query($institute_id: Int) {
    getNewEducationalStudents(institute_id: $institute_id) {
      student_id
      roll_no
      admission_no
      student_name
      student_mobile
      gender
      dob
      class_name
      section_name
      branch_name
      father_name
      father_phone
      mother_name
      mother_phone
      parent_name
      parent_mobile
    }
  }`;

const nowLocal = () => moment().format("YYYY-MM-DDTHH:mm");

class SingleRequestModal extends Component {
    constructor() {
        super();
        // dataClear starts at 0 so the very first open triggers the load below.
        this.state = { ...this.freshState(), dataClear: 0 };
    }

    freshState() {
        return {
            students: [],
            listLoaded: false,
            listLoader: false,
            selectedStudent: null,
            fromDate: nowLocal(),
            fromDateError: "",
            toDate: "",
            toDateError: "",
            reqMessage: "",
            receiverName: "",
            receiverNameError: "",
            receiverMobile: "",
            receiverMobileError: "",
            receiverRelation: "",
            submitted: false,
            loader: 0,
            submitError: "",
            dataClear: 1,
        };
    }

    componentDidUpdate() {
        // Modal just opened → reset the form, default the "from" date/time to now
        // and (re)load the student list.
        if (this.props.show === true && this.state.dataClear === 0) {
            this.setState({ ...this.freshState(), fromDate: nowLocal(), dataClear: 1 }, () => {
                this.fetchStudents();
            });
        }
        if (this.props.show === false && this.state.dataClear === 1) {
            this.setState({ dataClear: 0 });
        }
    }

    fetchStudents = async () => {
        this.setState({ listLoader: true });
        try {
            const result = await this.props.client.query({
                query: GET_STUDENTS,
                variables: { institute_id: parseInt(Cookies.get("institutionid")) },
                fetchPolicy: "network-only",
            });
            this.setState({
                students: result.data.getNewEducationalStudents || [],
                listLoaded: true,
                listLoader: false,
            });
        } catch (e) {
            this.setState({ listLoader: false, submitError: "Unable to load students. Please try again." });
        }
    };

    handleInputChange = (e) => {
        const { name, value } = e.target;
        this.setState({ [name]: value, submitError: "", submitted: false });
        if (name === "fromDate") this.setState({ fromDateError: "", toDateError: "" });
        if (name === "toDate") this.setState({ toDateError: "" });
        if (name === "receiverName") this.setState({ receiverNameError: "" });
        if (name === "receiverMobile") this.setState({ receiverMobileError: "" });
    };

    onSelectStudent = (opt) => {
        this.setState({ selectedStudent: opt ? opt.student : null, submitError: "", submitted: false });
    };

    validate = () => {
        const stu = this.state.selectedStudent;
        let valid = true;
        const next = {};

        if (!stu) { this.setState({ submitError: "Please select a student." }); return false; }

        // Receiver details are optional — only validate the mobile format if one is entered.
        const mob = this.state.receiverMobile.trim();
        if (mob && !/^\d{10}$/.test(mob)) { next.receiverMobileError = "Enter a valid 10-digit mobile number."; valid = false; }

        if (!this.state.fromDate) { next.fromDateError = "From date & time is required."; valid = false; }

        if (this.state.toDate && moment(this.state.toDate).isSameOrBefore(moment(this.state.fromDate))) {
            next.toDateError = "To date must be after the from date."; valid = false;
        }

        if (!stu.student_mobile) {
            this.setState({ submitError: "Selected student has no mobile number on record, so the request cannot be linked." });
            valid = false;
        }

        this.setState(next);
        return valid;
    };

    handleSubmit = () => {
        if (!this.validate()) return;
        const stu = this.state.selectedStudent;

        const params = {
            student_mobile: stu.student_mobile,
            from_datetime: moment(this.state.fromDate).unix().toString(),
            to_datetime: this.state.toDate ? moment(this.state.toDate).unix().toString() : "",
            request_title: "Individual Permission",
            request_message: this.state.reqMessage,
            receiver_name: this.state.receiverName.trim(),
            receiver_mobile: this.state.receiverMobile.trim(),
            receiver_relation: this.state.receiverRelation.trim(),
            approved_by: parseInt(Cookies.get("id")),
        };

        this.setState({ loader: 2, submitError: "" });
        this.createIndividualPermissions(params).catch(error => {
            this.setState({
                submitError: error.graphQLErrors ? error.graphQLErrors.map(x => x.message).join(", ") : "Something went wrong.",
                loader: 0,
            });
        });
    };

    createIndividualPermissions = async (params) => {
        await this.props.createIndividualPermissions({
            variables: { params },
            update: (store, { data }) => {
                if (data.createIndividualPermissions) {
                    this.setState({ submitted: true, loader: 0 });
                    setTimeout(() => {
                        // Refresh only the table data (no full page reload).
                        if (this.props.refetchPermissions) this.props.refetchPermissions();
                        this.props.onHide();
                    }, 1200);
                } else {
                    this.setState({ loader: 0, submitError: "Could not create the request. The student may not be linked to a parent." });
                }
            }
        });
    };

    detailItem = (label, value) => (
        <Col lg={4} sm={6} xs={12} className="mb-3" key={label}>
            <div className="perm-field-label">{label}</div>
            <div className="perm-field-value">{value || "--"}</div>
        </Col>
    );

    render() {
        const stu = this.state.selectedStudent;
        const options = this.state.students.map(s => ({
            value: s.student_id,
            label: `${s.student_name} - ${s.admission_no || "N/A"}`,
            student: s,
        }));

        return (
            <Modal {...this.props} size="lg" aria-labelledby="single-request-modal">
                <Modal.Header closeButton className="perm-modal-header-neutral" style={{ borderBottom: "none" }}>
                    <Modal.Title className="modal-title">
                        <i className="fas fa-user-plus mr-2" />New Permission
                    </Modal.Title>
                </Modal.Header>

                <Modal.Body style={{ background: "#F8FAFC", padding: "28px 28px 20px" }}>
                    {/* ── Student selector ── */}
                    <div style={{ background: "#fff", borderRadius: 12, padding: "20px 20px 16px", marginBottom: 18, boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}>
                        <p className="perm-section-heading"><i className="fas fa-user-graduate mr-1" />Select Student</p>
                        <Row>
                            <Col lg={8} sm={12} className="mb-2">
                                <label className="perm-input-label">Student Name - Admission No <span className="req">*</span></label>
                                <Select
                                    classNamePrefix="perm-student-select"
                                    placeholder={this.state.listLoader ? "Loading students…" : "Search by student name or admission no…"}
                                    isClearable
                                    isLoading={this.state.listLoader}
                                    noOptionsMessage={() => (this.state.listLoaded ? "No students found" : "Loading…")}
                                    options={options}
                                    value={stu ? { value: stu.student_id, label: `${stu.student_name} - ${stu.admission_no || "N/A"}` } : null}
                                    onChange={this.onSelectStudent}
                                    menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                                    styles={{
                                        control: (base, state) => ({
                                            ...base, minHeight: 44, borderRadius: 8, borderWidth: 1.5,
                                            borderColor: state.isFocused ? "#7c3aed" : "#e5e7eb", boxShadow: "none", fontSize: 14,
                                            "&:hover": { borderColor: "#7c3aed" },
                                        }),
                                        menuPortal: base => ({ ...base, zIndex: 12000 }),
                                        option: (base, state) => ({
                                            ...base, fontSize: 13,
                                            backgroundColor: state.isSelected ? "#7c3aed" : state.isFocused ? "#f5f3ff" : "#fff",
                                            color: state.isSelected ? "#fff" : "#1f2937",
                                        }),
                                    }}
                                />
                                {!stu && this.state.submitError && (
                                    <span className="perm-error-text"><i className="fas fa-exclamation-circle" />{this.state.submitError}</span>
                                )}
                            </Col>
                        </Row>
                    </div>

                    {stu && (
                        <>
                            {/* ── Student details ── */}
                            <div style={{ background: "#fff", borderRadius: 12, padding: "20px 20px 12px", marginBottom: 18, boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}>
                                <p className="perm-section-heading"><i className="fas fa-id-card mr-1" />Student Details</p>
                                <Row>
                                    {this.detailItem("Student Name", stu.student_name)}
                                    {this.detailItem("Admission No", stu.admission_no)}
                                    {this.detailItem("Roll No", stu.roll_no)}
                                    {this.detailItem("Class", stu.class_name)}
                                    {this.detailItem("Section", stu.section_name)}
                                    {this.detailItem("Branch", stu.branch_name)}
                                    {this.detailItem("Gender", stu.gender)}
                                    {this.detailItem("Date of Birth", stu.dob)}
                                    {this.detailItem("Student Mobile", stu.student_mobile)}
                                </Row>
                            </div>

                            {/* ── Parent details ── */}
                            <div style={{ background: "#fff", borderRadius: 12, padding: "20px 20px 12px", marginBottom: 18, boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}>
                                <p className="perm-section-heading"><i className="fas fa-users mr-1" />Parent Details</p>
                                <Row>
                                    {this.detailItem("Father Name", stu.father_name)}
                                    {this.detailItem("Father Phone", stu.father_phone)}
                                    {this.detailItem("Mother Name", stu.mother_name)}
                                    {this.detailItem("Mother Phone", stu.mother_phone)}
                                </Row>
                            </div>

                            {/* ── Receiver details ── */}
                            <div style={{ background: "#fff", borderRadius: 12, padding: "20px 20px 16px", marginBottom: 18, boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}>
                                <p className="perm-section-heading"><i className="fas fa-user-check mr-1" />Receiver Details</p>
                                <Row>
                                    <Col lg={4} sm={12} className="mb-3">
                                        <label className="perm-input-label">Receiver Name <span style={{ color: "#94A3B8", fontWeight: 400, textTransform: "none" }}>(optional)</span></label>
                                        <input
                                            type="text"
                                            name="receiverName"
                                            className={`perm-input${this.state.receiverNameError ? " is-invalid" : ""}`}
                                            placeholder="Enter receiver name"
                                            value={this.state.receiverName}
                                            onChange={this.handleInputChange}
                                        />
                                        {this.state.receiverNameError && (
                                            <span className="perm-error-text"><i className="fas fa-exclamation-circle" />{this.state.receiverNameError}</span>
                                        )}
                                    </Col>
                                    <Col lg={4} sm={12} className="mb-3">
                                        <label className="perm-input-label">Receiver Mobile <span style={{ color: "#94A3B8", fontWeight: 400, textTransform: "none" }}>(optional)</span></label>
                                        <input
                                            type="text"
                                            name="receiverMobile"
                                            maxLength={10}
                                            className={`perm-input${this.state.receiverMobileError ? " is-invalid" : ""}`}
                                            placeholder="10-digit mobile"
                                            value={this.state.receiverMobile}
                                            onChange={this.handleInputChange}
                                        />
                                        {this.state.receiverMobileError && (
                                            <span className="perm-error-text"><i className="fas fa-exclamation-circle" />{this.state.receiverMobileError}</span>
                                        )}
                                    </Col>
                                    <Col lg={4} sm={12} className="mb-3">
                                        <label className="perm-input-label">Receiver Relation <span style={{ color: "#94A3B8", fontWeight: 400, textTransform: "none" }}>(optional)</span></label>
                                        <input
                                            type="text"
                                            name="receiverRelation"
                                            className="perm-input"
                                            placeholder="e.g. Father, Uncle"
                                            value={this.state.receiverRelation}
                                            onChange={this.handleInputChange}
                                        />
                                    </Col>
                                </Row>
                            </div>

                            {/* ── Permission period ── */}
                            <div style={{ background: "#fff", borderRadius: 12, padding: "20px 20px 16px", boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}>
                                <p className="perm-section-heading"><i className="fas fa-calendar-alt mr-1" />Permission Period</p>
                                <Row>
                                    <Col lg={6} sm={12} className="mb-3">
                                        <label className="perm-input-label">From Date & Time <span className="req">*</span></label>
                                        <input
                                            type="datetime-local"
                                            name="fromDate"
                                            value={this.state.fromDate}
                                            onChange={this.handleInputChange}
                                            className={`perm-input${this.state.fromDateError ? " is-invalid" : ""}`}
                                            style={{ height: "44px" }}
                                        />
                                        {this.state.fromDateError && (
                                            <span className="perm-error-text"><i className="fas fa-exclamation-circle" />{this.state.fromDateError}</span>
                                        )}
                                    </Col>
                                    <Col lg={6} sm={12} className="mb-3">
                                        <label className="perm-input-label">To Date & Time <span style={{ color: "#94A3B8", fontWeight: 400, textTransform: "none" }}>(optional)</span></label>
                                        <input
                                            type="datetime-local"
                                            name="toDate"
                                            value={this.state.toDate}
                                            onChange={this.handleInputChange}
                                            min={this.state.fromDate || undefined}
                                            className={`perm-input${this.state.toDateError ? " is-invalid" : ""}`}
                                            style={{ height: "44px" }}
                                        />
                                        {this.state.toDateError && (
                                            <span className="perm-error-text"><i className="fas fa-exclamation-circle" />{this.state.toDateError}</span>
                                        )}
                                    </Col>
                                    <Col lg={12} sm={12} className="mb-3">
                                        <label className="perm-input-label">Request Message <span style={{ color: "#94A3B8", fontWeight: 400, textTransform: "none" }}>(optional)</span></label>
                                        <textarea
                                            name="reqMessage"
                                            onChange={this.handleInputChange}
                                            value={this.state.reqMessage}
                                            className="perm-input"
                                            rows={3}
                                            placeholder="Add a note for this request..."
                                        />
                                    </Col>
                                </Row>

                                <Row className="mt-1">
                                    <Col sm={12} className="d-flex align-items-center" style={{ gap: 14, flexWrap: "wrap", justifyContent: "flex-end" }}>
                                        <button className="btn-perm-submit" onClick={this.handleSubmit} disabled={this.state.loader === 2}>
                                            {this.state.loader === 2
                                                ? <span className="spinner-border spinner-border-sm" />
                                                : <i className="fas fa-paper-plane" />
                                            }
                                            Send Request
                                        </button>

                                        {this.state.submitted && this.state.loader === 0 && (
                                            <div className="perm-alert perm-alert-success">
                                                <i className="fas fa-check-circle" />Permission request submitted successfully!
                                            </div>
                                        )}
                                        {this.state.submitError && (
                                            <div className="perm-alert perm-alert-error">
                                                <i className="fas fa-exclamation-circle" />{this.state.submitError}
                                            </div>
                                        )}
                                    </Col>
                                </Row>
                            </div>
                        </>
                    )}
                </Modal.Body>
            </Modal>
        );
    }
}

export default withRouter(withApollo(
    compose(graphql(ADD_SINGLE_REQUEST_DETAILS, { name: "createIndividualPermissions" }))(SingleRequestModal)
));
